import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  FileText, Users, Wallet, TrendingDown, Download, Landmark, AlertCircle,
  Calendar as CalendarIcon, Database, Award, Plane, LifeBuoy, GraduationCap,
  Building2, LineChart, Trophy, Clock, Bell, Send, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const fmtNumber = (n) => (n || 0).toLocaleString('en-IN');

// Local summary tile component
const SummaryTile = ({ label, value, highlight, testid }) => (
  <div
    className={`p-3 rounded-md border ${highlight ? 'bg-primary/5 border-primary/30' : 'bg-muted/30'}`}
    data-testid={testid}
  >
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-base md:text-lg font-bold mt-1 ${highlight ? 'text-primary' : ''} break-words`}>
      {value}
    </p>
  </div>
);

const ReportsPage = () => {
  const { isHR } = useAuth();
  const [attendanceReport, setAttendanceReport] = useState([]);
  const [payrollReport, setPayrollReport] = useState(null);
  const [attritionReport, setAttritionReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Statutory state
  const [statutoryType, setStatutoryType] = useState('pf-ecr');
  const [statutoryMonth, setStatutoryMonth] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [statutoryQuarter, setStatutoryQuarter] = useState('Q3-2025-26');
  const [statutoryHalf, setStatutoryHalf] = useState('H2-2025');
  const [statutoryData, setStatutoryData] = useState(null);
  const [statutorySummary, setStatutorySummary] = useState(null);

  // Enhanced reports state
  const [masterData, setMasterData] = useState(null);
  const [masterFilters, setMasterFilters] = useState({ department: 'all', status: 'all' });
  const [perfData, setPerfData] = useState(null);
  const [perfPeriod, setPerfPeriod] = useState('');
  const [travelExpenseData, setTravelExpenseData] = useState(null);
  const [teRange, setTeRange] = useState(() => {
    const end = new Date();
    const start = new Date(); start.setDate(end.getDate() - 90);
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  });
  const [helpdeskData, setHelpdeskData] = useState(null);
  const [learningData, setLearningData] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [trendsMonths, setTrendsMonths] = useState(12);
  const [alumniData, setAlumniData] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [subsOpen, setSubsOpen] = useState(false);
  const [recipientsInput, setRecipientsInput] = useState({ master_data_weekly: '', headcount_monthly: '', performance_quarterly: '', attrition_monthly: '' });

  useEffect(() => {
    if (!isHR) return;
    axios.get(`${API_URL}/api/reports/statutory/summary`, { withCredentials: true })
      .then((r) => setStatutorySummary(r.data))
      .catch(() => {});
  }, [isHR]);

  // ===== Generic fetchers =====
  const safeFetch = async (url, setter, label) => {
    setLoading(true);
    try {
      const res = await axios.get(url, { withCredentials: true });
      setter(res.data);
    } catch (err) {
      console.error(`Fetch ${label} error:`, err);
      toast.error(err?.response?.data?.detail || `Failed to load ${label}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceReport = () => safeFetch(
    `${API_URL}/api/reports/attendance?start_date=${dateRange.start}&end_date=${dateRange.end}`,
    setAttendanceReport, 'attendance report',
  );
  const fetchPayrollReport = () => safeFetch(`${API_URL}/api/reports/payroll?month=${selectedMonth}`, setPayrollReport, 'payroll report');
  const fetchAttritionReport = () => safeFetch(`${API_URL}/api/reports/attrition?year=${selectedYear}`, setAttritionReport, 'attrition report');

  const fetchMasterData = () => {
    const params = new URLSearchParams();
    if (masterFilters.department !== 'all') params.append('department', masterFilters.department);
    if (masterFilters.status !== 'all') params.append('status', masterFilters.status);
    return safeFetch(`${API_URL}/api/reports/master-data?${params.toString()}`, setMasterData, 'master data');
  };
  const fetchPerformance = () => {
    const q = perfPeriod ? `?period=${perfPeriod}` : '';
    return safeFetch(`${API_URL}/api/reports/performance-analytics${q}`, setPerfData, 'performance analytics');
  };
  const fetchTravelExpense = () => safeFetch(
    `${API_URL}/api/reports/travel-expense?start_date=${teRange.start}&end_date=${teRange.end}`,
    setTravelExpenseData, 'travel & expense',
  );
  const fetchHelpdesk = () => safeFetch(`${API_URL}/api/reports/helpdesk`, setHelpdeskData, 'helpdesk');
  const fetchLearning = () => safeFetch(`${API_URL}/api/reports/learning`, setLearningData, 'learning');
  const fetchOrg = () => safeFetch(`${API_URL}/api/reports/org-structure`, setOrgData, 'org structure');
  const fetchTrends = () => safeFetch(`${API_URL}/api/reports/headcount-trends?months=${trendsMonths}`, setTrendsData, 'headcount trends');
  const fetchAlumni = () => safeFetch(`${API_URL}/api/reports/internship-alumni`, setAlumniData, 'internship alumni');

  const openSubscriptions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/subscriptions`, { withCredentials: true });
      setSubscriptions(res.data);
      setRecipientsInput({
        master_data_weekly: (res.data.master_data_weekly?.recipients || []).join(', '),
        headcount_monthly: (res.data.headcount_monthly?.recipients || []).join(', '),
        performance_quarterly: (res.data.performance_quarterly?.recipients || []).join(', '),
        attrition_monthly: (res.data.attrition_monthly?.recipients || []).join(', '),
      });
      setSubsOpen(true);
    } catch (err) {
      toast.error('Failed to load subscriptions');
    }
  };

  const saveSubscription = async (kind) => {
    const recipients = recipientsInput[kind]
      .split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
    const payload = { [kind]: { ...subscriptions[kind], recipients } };
    try {
      const res = await axios.put(`${API_URL}/api/reports/subscriptions`, payload, { withCredentials: true });
      setSubscriptions(res.data);
      toast.success('Subscription saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const toggleSubscription = async (kind, enabled) => {
    setSubscriptions(prev => ({ ...prev, [kind]: { ...prev[kind], enabled } }));
    try {
      await axios.put(`${API_URL}/api/reports/subscriptions`, { [kind]: { ...subscriptions[kind], enabled } }, { withCredentials: true });
      toast.success(enabled ? 'Subscription enabled' : 'Subscription paused');
    } catch (err) {
      toast.error('Failed to toggle subscription');
    }
  };

  const sendTestReport = async (kind) => {
    try {
      const res = await axios.post(`${API_URL}/api/reports/subscriptions/test`, { report: kind }, { withCredentials: true });
      toast.success(res.data.message || 'Test sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    }
  };

  const downloadMasterCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (masterFilters.department !== 'all') params.append('department', masterFilters.department);
      if (masterFilters.status !== 'all') params.append('status', masterFilters.status);
      const res = await axios.get(`${API_URL}/api/reports/master-data/csv?${params.toString()}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `employee_master_data_${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error('Failed to download CSV');
    }
  };

  // ===== Statutory helpers =====
  const STATUTORY_OPTIONS = [
    { value: 'pf-ecr', label: 'PF ECR (EPFO)', kind: 'monthly', desc: 'Monthly EPF return — due by 15th' },
    { value: 'esi', label: 'ESI Return', kind: 'monthly', desc: 'Monthly ESI return — due by 15th' },
    { value: 'pt', label: 'Professional Tax', kind: 'monthly', desc: 'Monthly PT collection (state-wise) — due by 10th' },
    { value: 'tds-24q', label: 'TDS Form 24Q', kind: 'quarterly', desc: 'Quarterly TDS return — due by 31st of next month' },
    { value: 'lwf', label: 'Labour Welfare Fund', kind: 'half-yearly', desc: 'Half-yearly LWF — due by Jul/Jan 31' },
  ];

  const buildStatutoryQuery = () => {
    const opt = STATUTORY_OPTIONS.find(o => o.value === statutoryType);
    if (opt?.kind === 'monthly') return `month=${statutoryMonth}`;
    if (opt?.kind === 'quarterly') return `quarter=${statutoryQuarter}`;
    if (opt?.kind === 'half-yearly') return `period=${statutoryHalf}`;
    return '';
  };

  const fetchStatutoryReport = async () => {
    setLoading(true);
    try {
      const q = buildStatutoryQuery();
      const res = await axios.get(`${API_URL}/api/reports/statutory/${statutoryType}?${q}`, { withCredentials: true });
      setStatutoryData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch statutory report');
    } finally {
      setLoading(false);
    }
  };

  const downloadStatutoryCSV = async () => {
    try {
      const q = buildStatutoryQuery();
      const res = await axios.get(`${API_URL}/api/reports/statutory/${statutoryType}/csv?${q}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = statutoryType === 'tds-24q' ? statutoryQuarter : (statutoryType === 'lwf' ? statutoryHalf : statutoryMonth);
      link.setAttribute('download', `${statutoryType}_${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error('Failed to download CSV');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-7 h-7 text-primary" />
            Reports & Analytics
          </h1>
          <p className="text-muted-foreground">Comprehensive HR data exports, performance, attrition, and operational insights</p>
        </div>
        {isHR && (
          <Button variant="outline" onClick={openSubscriptions} className="gap-2" data-testid="open-subscriptions">
            <Bell className="w-4 h-4" /> Subscriptions
          </Button>
        )}
      </div>

      <Tabs defaultValue="attendance">
        <TabsList className="flex-wrap h-auto justify-start gap-1">
          <TabsTrigger value="attendance" className="gap-2" data-testid="tab-attendance">
            <Users className="w-4 h-4" /> Attendance
          </TabsTrigger>
          {isHR && (
            <>
              <TabsTrigger value="master-data" className="gap-2" data-testid="tab-master-data">
                <Database className="w-4 h-4" /> Master Data
              </TabsTrigger>
              <TabsTrigger value="payroll" className="gap-2" data-testid="tab-payroll">
                <Wallet className="w-4 h-4" /> Payroll
              </TabsTrigger>
              <TabsTrigger value="performance" className="gap-2" data-testid="tab-performance">
                <Award className="w-4 h-4" /> Performance
              </TabsTrigger>
              <TabsTrigger value="travel-expense" className="gap-2" data-testid="tab-travel-expense">
                <Plane className="w-4 h-4" /> Travel & Expense
              </TabsTrigger>
              <TabsTrigger value="helpdesk" className="gap-2" data-testid="tab-helpdesk">
                <LifeBuoy className="w-4 h-4" /> Helpdesk
              </TabsTrigger>
              <TabsTrigger value="learning" className="gap-2" data-testid="tab-learning">
                <GraduationCap className="w-4 h-4" /> L&D
              </TabsTrigger>
              <TabsTrigger value="alumni" className="gap-2" data-testid="tab-alumni">
                <Sparkles className="w-4 h-4" /> Alumni
              </TabsTrigger>
              <TabsTrigger value="org-structure" className="gap-2" data-testid="tab-org-structure">
                <Building2 className="w-4 h-4" /> Org Structure
              </TabsTrigger>
              <TabsTrigger value="attrition" className="gap-2" data-testid="tab-attrition">
                <TrendingDown className="w-4 h-4" /> Attrition
              </TabsTrigger>
              <TabsTrigger value="statutory" className="gap-2" data-testid="tab-statutory">
                <Landmark className="w-4 h-4" /> Statutory
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Attendance Report */}
        <TabsContent value="attendance" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-lg">Attendance Report</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} className="w-[180px]" />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} className="w-[180px]" />
                </div>
                <div className="flex items-end">
                  <Button onClick={fetchAttendanceReport} disabled={loading} data-testid="generate-attendance-report">Generate Report</Button>
                </div>
              </div>
              {attendanceReport.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Employee ID</th><th>Present</th><th>Absent</th><th>WFH</th><th>Half Days</th></tr></thead>
                    <tbody>
                      {attendanceReport.map((row, i) => (
                        <tr key={i}>
                          <td className="font-mono text-xs">{row._id?.slice(0, 8) || '-'}</td>
                          <td className="text-[hsl(var(--success))]">{row.present_days}</td>
                          <td className="text-destructive">{row.absent_days}</td>
                          <td className="text-[hsl(var(--ai-accent))]">{row.wfh_days}</td>
                          <td className="text-[hsl(var(--warning))]">{row.half_days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== Master Data ====== */}
        {isHR && (
          <TabsContent value="master-data" className="mt-4 space-y-4" data-testid="master-data-tab-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Database className="w-4 h-4 text-primary" /> Employee Master Data</CardTitle>
                <CardDescription>Single source of truth for HR data — filter, view and export to CSV</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select value={masterFilters.department} onValueChange={(v) => setMasterFilters({ ...masterFilters, department: v })}>
                      <SelectTrigger data-testid="master-dept-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {(masterData?.by_department || []).map(d => <SelectItem key={d.department} value={d.department}>{d.department}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={masterFilters.status} onValueChange={(v) => setMasterFilters({ ...masterFilters, status: v })}>
                      <SelectTrigger data-testid="master-status-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={fetchMasterData} disabled={loading} className="flex-1" data-testid="generate-master-data">Generate</Button>
                    {masterData && (
                      <Button variant="outline" onClick={downloadMasterCSV} className="gap-2" data-testid="download-master-csv">
                        <Download className="w-4 h-4" /> CSV
                      </Button>
                    )}
                  </div>
                </div>

                {masterData && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <SummaryTile label="Total Employees" value={masterData.total} highlight testid="master-total" />
                      <SummaryTile label="Departments" value={masterData.by_department.length} />
                      <SummaryTile label="Active" value={masterData.by_status.find(s => s.status === 'active')?.count || 0} />
                      <SummaryTile label="Inactive" value={masterData.by_status.find(s => s.status === 'inactive')?.count || 0} />
                    </div>

                    {/* Department breakdown */}
                    <div>
                      <h4 className="text-sm font-medium mb-2">Headcount by Department</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {masterData.by_department.map(d => (
                          <div key={d.department} className="p-3 rounded-md border bg-muted/30">
                            <p className="text-xs text-muted-foreground">{d.department}</p>
                            <p className="text-lg font-bold mt-1">{d.count}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Rows table */}
                    <div className="overflow-x-auto rounded-md border" data-testid="master-rows-table">
                      <table className="data-table w-full text-sm">
                        <thead><tr>{masterData.columns.map(c => <th key={c} className="capitalize whitespace-nowrap">{c.replace(/_/g, ' ')}</th>)}</tr></thead>
                        <tbody>
                          {masterData.rows.map((row, i) => (
                            <tr key={i}>
                              {masterData.columns.map(c => <td key={c} className="whitespace-nowrap text-xs">{String(row[c] ?? '—')}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Payroll Report */}
        {isHR && (
          <TabsContent value="payroll" className="mt-4 space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-lg">Payroll Report</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="w-[180px]" />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={fetchPayrollReport} disabled={loading} data-testid="generate-payroll-report">Generate Report</Button>
                  </div>
                </div>
                {payrollReport && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <SummaryTile label="Employees" value={payrollReport.employee_count} />
                    <SummaryTile label="Total Gross" value={fmtCurrency(payrollReport.total_gross_salary)} />
                    <SummaryTile label="Total Net" value={fmtCurrency(payrollReport.total_net_salary)} highlight />
                    <SummaryTile label="Total TDS" value={fmtCurrency(payrollReport.total_tds)} />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ====== Performance Analytics ====== */}
        {isHR && (
          <TabsContent value="performance" className="mt-4 space-y-4" data-testid="performance-tab-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4 text-primary" /> Performance Analytics</CardTitle>
                <CardDescription>Rating distribution, department averages, and top/bottom performers</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div className="space-y-2">
                    <Label>Review Period</Label>
                    <Select value={perfPeriod || 'all'} onValueChange={(v) => setPerfPeriod(v === 'all' ? '' : v)}>
                      <SelectTrigger data-testid="perf-period"><SelectValue placeholder="All periods" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Periods</SelectItem>
                        {(perfData?.available_periods || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Button onClick={fetchPerformance} disabled={loading} data-testid="generate-performance">Generate</Button>
                  </div>
                </div>

                {perfData && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <SummaryTile label="Total Reviews" value={perfData.total_reviews} highlight testid="perf-total" />
                      <SummaryTile label="Rating Levels" value={perfData.rating_distribution.length} />
                      <SummaryTile label="Departments Reviewed" value={perfData.department_averages.length} />
                      <SummaryTile label="Top Performers" value={perfData.top_performers.length} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Rating distribution */}
                      <div>
                        <h4 className="text-sm font-medium mb-2">Rating Distribution</h4>
                        <div className="space-y-2">
                          {perfData.rating_distribution.map(r => (
                            <div key={r.rating} className="flex items-center gap-3">
                              <Badge variant="secondary" className="capitalize w-24 justify-center">{r.rating}</Badge>
                              <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                                <div className="h-full bg-primary" style={{ width: `${Math.min(100, (r.count / perfData.total_reviews) * 100)}%` }} />
                              </div>
                              <span className="text-sm font-mono w-10 text-right">{r.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Department averages */}
                      <div>
                        <h4 className="text-sm font-medium mb-2">Department Averages</h4>
                        <div className="space-y-2">
                          {perfData.department_averages.map(d => (
                            <div key={d.department} className="flex items-center gap-3">
                              <span className="text-sm w-32 truncate">{d.department}</span>
                              <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                                <div className="h-full bg-[hsl(var(--success))]" style={{ width: `${Math.min(100, d.avg_score)}%` }} />
                              </div>
                              <span className="text-sm font-mono w-16 text-right">{d.avg_score}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Top performers */}
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-yellow-500" /> Top Performers</h4>
                      <div className="overflow-x-auto rounded-md border">
                        <table className="data-table w-full text-sm">
                          <thead><tr><th>Rank</th><th>Employee</th><th>Department</th><th>Score</th><th>Rating</th><th>Period</th></tr></thead>
                          <tbody>
                            {perfData.top_performers.map((p, i) => (
                              <tr key={i}>
                                <td className="font-mono">#{i + 1}</td>
                                <td>{p.employee_name}</td>
                                <td>{p.department}</td>
                                <td className="font-bold text-[hsl(var(--success))]">{p.overall_score}</td>
                                <td><Badge variant="outline" className="capitalize">{p.rating}</Badge></td>
                                <td className="text-xs text-muted-foreground">{p.period}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ====== Travel & Expense ====== */}
        {isHR && (
          <TabsContent value="travel-expense" className="mt-4 space-y-4" data-testid="travel-expense-tab-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Plane className="w-4 h-4 text-primary" /> Travel & Expense Report</CardTitle>
                <CardDescription>Combined travel + expense analytics with cost breakdowns</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input type="date" value={teRange.start} onChange={(e) => setTeRange({ ...teRange, start: e.target.value })} data-testid="te-start" />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input type="date" value={teRange.end} onChange={(e) => setTeRange({ ...teRange, end: e.target.value })} data-testid="te-end" />
                  </div>
                  <div>
                    <Button onClick={fetchTravelExpense} disabled={loading} data-testid="generate-te">Generate</Button>
                  </div>
                </div>

                {travelExpenseData && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <SummaryTile label="Travel Requests" value={travelExpenseData.travel.total_requests} />
                      <SummaryTile label="Expense Claims" value={travelExpenseData.expenses.total_claims} />
                      <SummaryTile label="Total Spent" value={fmtCurrency(travelExpenseData.expenses.total_amount)} highlight testid="te-total" />
                      <SummaryTile label="Pending Approval" value={fmtCurrency(travelExpenseData.expenses.pending_amount)} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Expenses by Category</h4>
                        <div className="space-y-2">
                          {travelExpenseData.expenses.by_category.map(c => (
                            <div key={c.category} className="flex justify-between items-center p-2 rounded bg-muted/30">
                              <span className="text-sm capitalize">{c.category}</span>
                              <span className="font-mono text-sm">{fmtCurrency(c.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium mb-2">Travel by Category</h4>
                        <div className="space-y-2">
                          {travelExpenseData.travel.by_category.map(c => (
                            <div key={c.category} className="flex justify-between items-center p-2 rounded bg-muted/30">
                              <span className="text-sm capitalize">{c.category}</span>
                              <Badge variant="secondary">{c.count}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2">Top Spenders</h4>
                      <div className="overflow-x-auto rounded-md border">
                        <table className="data-table w-full text-sm">
                          <thead><tr><th>Rank</th><th>Employee</th><th>Total Spent</th></tr></thead>
                          <tbody>
                            {travelExpenseData.expenses.top_spenders.map((s, i) => (
                              <tr key={i}>
                                <td className="font-mono">#{i + 1}</td>
                                <td>{s.employee_name}</td>
                                <td className="font-mono font-bold">{fmtCurrency(s.total_spent)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ====== Helpdesk ====== */}
        {isHR && (
          <TabsContent value="helpdesk" className="mt-4 space-y-4" data-testid="helpdesk-tab-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-primary" /> Helpdesk Analytics</CardTitle>
                <CardDescription>Ticket volume, SLA compliance, and category breakdown (last 90 days)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={fetchHelpdesk} disabled={loading} data-testid="generate-helpdesk">Generate</Button>
                {helpdeskData && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <SummaryTile label="Total Tickets" value={helpdeskData.total_tickets} highlight />
                      <SummaryTile label="Open" value={helpdeskData.open_tickets} />
                      <SummaryTile label="Resolved" value={helpdeskData.resolved_tickets} />
                      <SummaryTile label="SLA Breached" value={helpdeskData.sla_breached} />
                      <SummaryTile label="Avg Resolution (hrs)" value={helpdeskData.avg_resolution_hours} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">By Status</h4>
                        <div className="space-y-2">
                          {helpdeskData.by_status.map(s => (
                            <div key={s.status} className="flex justify-between items-center p-2 rounded bg-muted/30">
                              <span className="text-sm capitalize">{s.status.replace(/_/g, ' ')}</span>
                              <Badge variant="secondary">{s.count}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">By Category</h4>
                        <div className="space-y-2">
                          {helpdeskData.by_category.map(c => (
                            <div key={c.category} className="flex justify-between items-center p-2 rounded bg-muted/30">
                              <span className="text-sm capitalize">{c.category}</span>
                              <Badge variant="secondary">{c.count}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">By Priority</h4>
                        <div className="space-y-2">
                          {helpdeskData.by_priority.map(p => (
                            <div key={p.priority} className="flex justify-between items-center p-2 rounded bg-muted/30">
                              <span className="text-sm capitalize">{p.priority}</span>
                              <Badge variant={p.priority === 'high' || p.priority === 'urgent' ? 'destructive' : 'secondary'}>{p.count}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ====== Learning & Development ====== */}
        {isHR && (
          <TabsContent value="learning" className="mt-4 space-y-4" data-testid="learning-tab-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><GraduationCap className="w-4 h-4 text-primary" /> Learning & Development</CardTitle>
                <CardDescription>Training completions, certifications, and top learners</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={fetchLearning} disabled={loading} data-testid="generate-learning">Generate</Button>
                {learningData && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <SummaryTile label="Trainings Completed" value={learningData.total_trainings_completed} highlight />
                      <SummaryTile label="Courses Completed" value={learningData.total_courses_completed} />
                      <SummaryTile label="Unique Learners" value={learningData.unique_learners} />
                      <SummaryTile label="First-Attempt Pass %" value={`${learningData.first_attempt_pass_rate}%`} />
                      <SummaryTile label="Witties Awarded" value={fmtNumber(learningData.total_witties_awarded)} />
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2">Department Rollup</h4>
                      <div className="overflow-x-auto rounded-md border">
                        <table className="data-table w-full text-sm">
                          <thead><tr><th>Department</th><th>Employees</th><th>Trainings</th><th>Courses</th><th>Credits</th><th>Witties</th></tr></thead>
                          <tbody>
                            {learningData.department_rollup.map(d => (
                              <tr key={d.department}>
                                <td className="font-medium">{d.department}</td>
                                <td>{d.employees}</td>
                                <td>{d.trainings}</td>
                                <td>{d.courses}</td>
                                <td>{d.credits}</td>
                                <td>{d.witties}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-yellow-500" /> Top Learners</h4>
                      <div className="overflow-x-auto rounded-md border">
                        <table className="data-table w-full text-sm">
                          <thead><tr><th>Rank</th><th>Employee</th><th>Department</th><th>Trainings</th><th>Courses</th><th>First-Attempt Pass</th><th>Witties</th></tr></thead>
                          <tbody>
                            {learningData.top_learners.map((l, i) => (
                              <tr key={i}>
                                <td className="font-mono">#{i + 1}</td>
                                <td>{l.employee_name}</td>
                                <td className="text-xs">{l.department}</td>
                                <td>{l.trainings_completed}</td>
                                <td>{l.courses_completed}</td>
                                <td>{l.first_attempt_passes}</td>
                                <td>{l.witties_earned}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ====== Internship Alumni ====== */}
        {isHR && (
          <TabsContent value="alumni" className="mt-4 space-y-4" data-testid="alumni-tab-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Internship → FTE Alumni Retention</CardTitle>
                <CardDescription>Track which interns converted to FTEs and how many are still with the company — the ROI of your internship program</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={fetchAlumni} disabled={loading} data-testid="generate-alumni">Generate</Button>
                {alumniData && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <SummaryTile label="Total Converted" value={alumniData.total_converted} highlight />
                      <SummaryTile label="Still With Us" value={alumniData.retained} />
                      <SummaryTile label="Left" value={alumniData.left} />
                      <SummaryTile label="Retention Rate" value={`${alumniData.retention_rate}%`} highlight />
                    </div>

                    {alumniData.total_converted > 0 && (
                      <>
                        <div>
                          <h4 className="text-sm font-medium mb-2">Tenure Distribution (Active Alumni)</h4>
                          <div className="space-y-2">
                            {alumniData.buckets.map(b => (
                              <div key={b.bucket} className="flex items-center gap-3">
                                <span className="text-sm w-20">{b.bucket}</span>
                                <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                                  <div className="h-full bg-emerald-500"
                                       style={{ width: `${alumniData.retained ? (b.count / alumniData.retained) * 100 : 0}%` }} />
                                </div>
                                <span className="text-sm font-mono w-10 text-right">{b.count}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium mb-2">Alumni Roster</h4>
                          <div className="overflow-x-auto rounded-md border">
                            <table className="data-table w-full text-sm" data-testid="alumni-table">
                              <thead>
                                <tr>
                                  <th>Name</th><th>University</th><th>Mentor</th><th>FTE Code</th>
                                  <th>Designation</th><th>Tenure</th><th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {alumniData.rows.map((r, i) => (
                                  <tr key={i}>
                                    <td className="font-medium">{r.intern_name}</td>
                                    <td className="text-xs">{r.university || '—'}</td>
                                    <td className="text-xs">{r.mentor || '—'}</td>
                                    <td className="font-mono text-xs">{r.fte_emp_code}</td>
                                    <td className="text-xs">{r.fte_designation || '—'}</td>
                                    <td className="text-xs">{r.tenure_months !== null ? `${r.tenure_months} mo` : '—'}</td>
                                    <td>
                                      {r.is_retained
                                        ? <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Retained</Badge>
                                        : <Badge variant="secondary" className="capitalize">{r.current_status}</Badge>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    )}

                    {alumniData.total_converted === 0 && (
                      <p className="text-sm text-muted-foreground py-6 text-center">No intern conversions yet. Convert an intern from <a href="/internships" className="underline">Internships</a> to start tracking.</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}


        {/* ====== Org Structure ====== */}
        {isHR && (
          <TabsContent value="org-structure" className="mt-4 space-y-4" data-testid="org-structure-tab-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" /> Organization Structure</CardTitle>
                <CardDescription>Headcount distribution by department, designation, manager, and tenure</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={fetchOrg} disabled={loading} data-testid="generate-org">Generate</Button>
                {orgData && (
                  <>
                    <SummaryTile label="Total Active Employees" value={orgData.total_active_employees} highlight testid="org-total" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">By Department</h4>
                        <div className="space-y-2">
                          {orgData.by_department.map(d => (
                            <div key={d.department} className="flex items-center gap-3">
                              <span className="text-sm w-32 truncate">{d.department}</span>
                              <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                                <div className="h-full bg-primary" style={{ width: `${(d.headcount / orgData.total_active_employees) * 100}%` }} />
                              </div>
                              <span className="text-sm font-mono w-10 text-right">{d.headcount}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> Tenure Distribution</h4>
                        <div className="space-y-2">
                          {orgData.by_tenure.map(t => (
                            <div key={t.bucket} className="flex items-center gap-3">
                              <span className="text-sm w-20">{t.bucket}</span>
                              <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                                <div className="h-full bg-[hsl(var(--ai-accent))]" style={{ width: `${orgData.total_active_employees ? (t.count / orgData.total_active_employees) * 100 : 0}%` }} />
                              </div>
                              <span className="text-sm font-mono w-10 text-right">{t.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium mb-2">By Designation</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {orgData.by_designation.map(d => (
                          <div key={d.designation} className="p-3 rounded-md border bg-muted/30">
                            <p className="text-xs text-muted-foreground truncate">{d.designation}</p>
                            <p className="text-lg font-bold mt-1">{d.headcount}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {orgData.by_manager.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2">Top Managers (Team Size)</h4>
                        <div className="overflow-x-auto rounded-md border">
                          <table className="data-table w-full text-sm">
                            <thead><tr><th>Manager</th><th>Direct Reports</th></tr></thead>
                            <tbody>
                              {orgData.by_manager.map(m => (
                                <tr key={m.manager}><td>{m.manager}</td><td className="font-bold">{m.team_size}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ====== Attrition (with trends) ====== */}
        {isHR && (
          <TabsContent value="attrition" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><TrendingDown className="w-4 h-4 text-primary" /> Attrition & Headcount Trends</CardTitle>
                <CardDescription>Annual attrition snapshot + monthly trend analysis</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2024">2024</SelectItem>
                        <SelectItem value="2025">2025</SelectItem>
                        <SelectItem value="2026">2026</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchAttritionReport} disabled={loading} data-testid="generate-attrition-report">Annual Snapshot</Button>
                </div>

                {attritionReport && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <SummaryTile label="Total Employees" value={attritionReport.total_employees} />
                    <SummaryTile label={`Left in ${selectedYear}`} value={attritionReport.employees_left} />
                    <SummaryTile label="Attrition Rate" value={`${attritionReport.attrition_rate}%`} highlight />
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="space-y-2">
                      <Label>Trend Window</Label>
                      <Select value={trendsMonths.toString()} onValueChange={(v) => setTrendsMonths(parseInt(v))}>
                        <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="6">Last 6 months</SelectItem>
                          <SelectItem value="12">Last 12 months</SelectItem>
                          <SelectItem value="24">Last 24 months</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={fetchTrends} disabled={loading} className="gap-2" data-testid="generate-trends">
                      <LineChart className="w-4 h-4" /> Monthly Trends
                    </Button>
                  </div>

                  {trendsData && (
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <SummaryTile label="Avg Headcount" value={trendsData.summary.avg_headcount} />
                        <SummaryTile label="Total Joinings" value={trendsData.summary.total_joinings} />
                        <SummaryTile label="Total Exits" value={trendsData.summary.total_exits} />
                        <SummaryTile label="Overall Attrition %" value={`${trendsData.summary.overall_attrition_rate}%`} highlight />
                      </div>

                      <div className="overflow-x-auto rounded-md border">
                        <table className="data-table w-full text-sm" data-testid="trends-table">
                          <thead><tr><th>Month</th><th>Headcount</th><th>Joinings</th><th>Exits</th><th>Attrition %</th></tr></thead>
                          <tbody>
                            {trendsData.monthly.map(m => (
                              <tr key={m.month}>
                                <td className="font-mono">{m.month}</td>
                                <td>{m.headcount}</td>
                                <td className="text-[hsl(var(--success))]">{m.joinings > 0 ? `+${m.joinings}` : 0}</td>
                                <td className="text-destructive">{m.exits > 0 ? `-${m.exits}` : 0}</td>
                                <td className="font-bold">{m.attrition_rate}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Statutory Reports */}
        {isHR && (
          <TabsContent value="statutory" className="mt-4 space-y-4" data-testid="statutory-tab-content">
            {statutorySummary && (
              <Card className="border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600" /> Upcoming Filings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {statutorySummary.filings.map((f, idx) => (
                      <div key={idx} className="p-3 rounded-md border bg-card text-sm">
                        <p className="font-medium">{f.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{f.frequency} • Due {f.due_day}th</p>
                        <p className="text-xs mt-1">For: <span className="font-mono">{f.for_period}</span></p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-primary" /> Statutory Compliance Reports
                </CardTitle>
                <CardDescription>Generate quarterly returns and statutory filings — PF ECR, ESI, PT, TDS 24Q, LWF</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                  <div className="space-y-2">
                    <Label>Report Type</Label>
                    <Select value={statutoryType} onValueChange={(v) => { setStatutoryType(v); setStatutoryData(null); }}>
                      <SelectTrigger data-testid="statutory-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUTORY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {STATUTORY_OPTIONS.find(o => o.value === statutoryType)?.kind === 'monthly' && (
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Input type="month" value={statutoryMonth} onChange={(e) => setStatutoryMonth(e.target.value)} data-testid="statutory-month" />
                    </div>
                  )}
                  {STATUTORY_OPTIONS.find(o => o.value === statutoryType)?.kind === 'quarterly' && (
                    <div className="space-y-2">
                      <Label>Quarter (FY)</Label>
                      <Select value={statutoryQuarter} onValueChange={setStatutoryQuarter}>
                        <SelectTrigger data-testid="statutory-quarter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Q1-2025-26">Q1 2025-26 (Apr–Jun)</SelectItem>
                          <SelectItem value="Q2-2025-26">Q2 2025-26 (Jul–Sep)</SelectItem>
                          <SelectItem value="Q3-2025-26">Q3 2025-26 (Oct–Dec)</SelectItem>
                          <SelectItem value="Q4-2025-26">Q4 2025-26 (Jan–Mar)</SelectItem>
                          <SelectItem value="Q1-2024-25">Q1 2024-25 (Apr–Jun)</SelectItem>
                          <SelectItem value="Q2-2024-25">Q2 2024-25 (Jul–Sep)</SelectItem>
                          <SelectItem value="Q3-2024-25">Q3 2024-25 (Oct–Dec)</SelectItem>
                          <SelectItem value="Q4-2024-25">Q4 2024-25 (Jan–Mar)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {STATUTORY_OPTIONS.find(o => o.value === statutoryType)?.kind === 'half-yearly' && (
                    <div className="space-y-2">
                      <Label>Half-Year</Label>
                      <Select value={statutoryHalf} onValueChange={setStatutoryHalf}>
                        <SelectTrigger data-testid="statutory-half"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="H1-2025">H1 2025 (Jan–Jun)</SelectItem>
                          <SelectItem value="H2-2025">H2 2025 (Jul–Dec)</SelectItem>
                          <SelectItem value="H1-2026">H1 2026 (Jan–Jun)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button onClick={fetchStatutoryReport} disabled={loading} className="gap-2 flex-1" data-testid="generate-statutory-btn">
                      <CalendarIcon className="w-4 h-4" /> Generate
                    </Button>
                    {statutoryData && (
                      <Button variant="outline" onClick={downloadStatutoryCSV} className="gap-2" data-testid="download-statutory-csv">
                        <Download className="w-4 h-4" /> CSV
                      </Button>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">{STATUTORY_OPTIONS.find(o => o.value === statutoryType)?.desc}</p>

                {statutoryData && (
                  <div className="space-y-4 pt-3 border-t">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {statutoryType === 'pf-ecr' && (
                        <>
                          <SummaryTile label="Employees" value={statutoryData.employee_count} />
                          <SummaryTile label="Employee PF" value={fmtCurrency(statutoryData.total_employee_pf)} />
                          <SummaryTile label="EPS + Employer PF" value={fmtCurrency((statutoryData.total_eps || 0) + (statutoryData.total_employer_pf || 0))} />
                          <SummaryTile label="Total Remittance" value={fmtCurrency(statutoryData.total_remittance)} highlight />
                        </>
                      )}
                      {statutoryType === 'esi' && (
                        <>
                          <SummaryTile label="Covered Employees" value={statutoryData.covered_employee_count} />
                          <SummaryTile label="Employee Contribution" value={fmtCurrency(statutoryData.total_employee_contribution)} />
                          <SummaryTile label="Employer Contribution" value={fmtCurrency(statutoryData.total_employer_contribution)} />
                          <SummaryTile label="Total Remittance" value={fmtCurrency(statutoryData.total_remittance)} highlight />
                        </>
                      )}
                      {statutoryType === 'pt' && (
                        <>
                          <SummaryTile label="Employees" value={statutoryData.employee_count} />
                          <SummaryTile label="Total PT Collected" value={fmtCurrency(statutoryData.total_pt_collected)} highlight />
                          <SummaryTile label="States" value={statutoryData.state_summary?.length || 0} />
                        </>
                      )}
                      {statutoryType === 'tds-24q' && (
                        <>
                          <SummaryTile label="Deductees" value={statutoryData.deductee_count} />
                          <SummaryTile label="Total Taxable Paid" value={fmtCurrency(statutoryData.total_taxable_paid)} />
                          <SummaryTile label="Total TDS Deducted" value={fmtCurrency(statutoryData.total_tds_deducted)} highlight />
                          <SummaryTile label="Months" value={statutoryData.months_covered?.join(', ')} />
                        </>
                      )}
                      {statutoryType === 'lwf' && (
                        <>
                          <SummaryTile label="Covered Employees" value={statutoryData.covered_employee_count} />
                          <SummaryTile label="Employee Contribution" value={fmtCurrency(statutoryData.total_employee_contribution)} />
                          <SummaryTile label="Employer Contribution" value={fmtCurrency(statutoryData.total_employer_contribution)} />
                          <SummaryTile label="Total Remittance" value={fmtCurrency(statutoryData.total_remittance)} highlight />
                        </>
                      )}
                    </div>

                    {statutoryData.rows && statutoryData.rows.length > 0 ? (
                      <div className="overflow-x-auto rounded-md border" data-testid="statutory-rows-table">
                        <table className="data-table w-full text-sm">
                          <thead>
                            <tr>{Object.keys(statutoryData.rows[0]).map(k => <th key={k} className="capitalize whitespace-nowrap">{k.replace(/_/g, ' ')}</th>)}</tr>
                          </thead>
                          <tbody>
                            {statutoryData.rows.map((row, i) => (
                              <tr key={i}>
                                {Object.keys(statutoryData.rows[0]).map(k => (
                                  <td key={k} className="whitespace-nowrap text-xs">
                                    {typeof row[k] === 'number' && k.includes('contribution') ? fmtCurrency(row[k]) :
                                     typeof row[k] === 'number' && (k.includes('wages') || k.includes('total') || k.includes('pt') || k.includes('tds') || k === 'epf_contribution_employee' || k === 'epf_contribution_employer' || k === 'eps_contribution_employer' || k === 'edli_contribution') ? fmtCurrency(row[k]) :
                                     String(row[k] ?? '—')}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-6 text-center">No data found for this period. Make sure payslips are generated.</p>
                    )}

                    {statutoryType === 'pt' && statutoryData.state_summary?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2">State-wise Summary</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                          {statutoryData.state_summary.map(s => (
                            <div key={s.state} className="p-3 rounded-md border bg-muted/30">
                              <p className="text-sm font-medium">{s.state}</p>
                              <p className="text-xs text-muted-foreground">{s.employee_count} employees</p>
                              <p className="text-sm font-mono mt-1">{fmtCurrency(s.total_pt)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ====== Subscriptions Dialog ====== */}
      <Dialog open={subsOpen} onOpenChange={setSubsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-primary" /> Scheduled Report Subscriptions</DialogTitle>
            <DialogDescription>
              Auto-email key reports to leadership on a recurring schedule. Recipients are comma-separated emails.
            </DialogDescription>
          </DialogHeader>
          {subscriptions && (
            <div className="space-y-5 mt-2">
              {/* Master Data Weekly */}
              <div className="rounded-md border p-4 space-y-3" data-testid="sub-master-data">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium flex items-center gap-2"><Database className="w-4 h-4" /> Master Data — Weekly</p>
                    <p className="text-xs text-muted-foreground">Employee Master Data CSV attached + department breakdown summary</p>
                  </div>
                  <Switch
                    checked={!!subscriptions.master_data_weekly?.enabled}
                    onCheckedChange={(v) => toggleSubscription('master_data_weekly', v)}
                    data-testid="toggle-master-data-sub"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Day</Label>
                    <Select
                      value={subscriptions.master_data_weekly?.day_of_week || 'mon'}
                      onValueChange={(v) => setSubscriptions(prev => ({ ...prev, master_data_weekly: { ...prev.master_data_weekly, day_of_week: v } }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Hour (IST)</Label>
                    <Input type="number" min="0" max="23"
                           value={subscriptions.master_data_weekly?.hour ?? 9}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, master_data_weekly: { ...prev.master_data_weekly, hour: parseInt(e.target.value) || 9 } }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Minute</Label>
                    <Input type="number" min="0" max="59"
                           value={subscriptions.master_data_weekly?.minute ?? 0}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, master_data_weekly: { ...prev.master_data_weekly, minute: parseInt(e.target.value) || 0 } }))} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Recipients (comma-separated emails)</Label>
                  <Input value={recipientsInput.master_data_weekly}
                         onChange={(e) => setRecipientsInput(prev => ({ ...prev, master_data_weekly: e.target.value }))}
                         placeholder="founder@wisewit.ai, hr-lead@wisewit.ai" data-testid="recipients-master-data" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => saveSubscription('master_data_weekly')} data-testid="save-master-data-sub">Save</Button>
                  <Button size="sm" variant="outline" onClick={() => sendTestReport('master_data_weekly')} className="gap-1" data-testid="test-master-data-sub">
                    <Send className="w-3.5 h-3.5" /> Send Test Now
                  </Button>
                </div>
              </div>

              {/* Headcount Monthly */}
              <div className="rounded-md border p-4 space-y-3" data-testid="sub-headcount">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium flex items-center gap-2"><LineChart className="w-4 h-4" /> Headcount & Attrition — Monthly</p>
                    <p className="text-xs text-muted-foreground">12-month trend with joinings, exits, and attrition rate</p>
                  </div>
                  <Switch
                    checked={!!subscriptions.headcount_monthly?.enabled}
                    onCheckedChange={(v) => toggleSubscription('headcount_monthly', v)}
                    data-testid="toggle-headcount-sub"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Day of Month</Label>
                    <Input type="number" min="1" max="28"
                           value={subscriptions.headcount_monthly?.day_of_month ?? 1}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, headcount_monthly: { ...prev.headcount_monthly, day_of_month: parseInt(e.target.value) || 1 } }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Hour (IST)</Label>
                    <Input type="number" min="0" max="23"
                           value={subscriptions.headcount_monthly?.hour ?? 9}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, headcount_monthly: { ...prev.headcount_monthly, hour: parseInt(e.target.value) || 9 } }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Minute</Label>
                    <Input type="number" min="0" max="59"
                           value={subscriptions.headcount_monthly?.minute ?? 0}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, headcount_monthly: { ...prev.headcount_monthly, minute: parseInt(e.target.value) || 0 } }))} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Recipients (comma-separated emails)</Label>
                  <Input value={recipientsInput.headcount_monthly}
                         onChange={(e) => setRecipientsInput(prev => ({ ...prev, headcount_monthly: e.target.value }))}
                         placeholder="founder@wisewit.ai, ceo@wisewit.ai" data-testid="recipients-headcount" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => saveSubscription('headcount_monthly')} data-testid="save-headcount-sub">Save</Button>
                  <Button size="sm" variant="outline" onClick={() => sendTestReport('headcount_monthly')} className="gap-1" data-testid="test-headcount-sub">
                    <Send className="w-3.5 h-3.5" /> Send Test Now
                  </Button>
                </div>
              </div>

              {/* Performance Quarterly */}
              <div className="rounded-md border p-4 space-y-3" data-testid="sub-performance">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium flex items-center gap-2"><Award className="w-4 h-4" /> Performance Snapshot — Quarterly</p>
                    <p className="text-xs text-muted-foreground">Rating distribution, dept averages, top performers — runs on the 1st of Jan/Apr/Jul/Oct</p>
                  </div>
                  <Switch
                    checked={!!subscriptions.performance_quarterly?.enabled}
                    onCheckedChange={(v) => toggleSubscription('performance_quarterly', v)}
                    data-testid="toggle-performance-sub"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Day of Quarter (1st month)</Label>
                    <Input type="number" min="1" max="28"
                           value={subscriptions.performance_quarterly?.day_of_month ?? 1}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, performance_quarterly: { ...prev.performance_quarterly, day_of_month: parseInt(e.target.value) || 1 } }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Hour (IST)</Label>
                    <Input type="number" min="0" max="23"
                           value={subscriptions.performance_quarterly?.hour ?? 9}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, performance_quarterly: { ...prev.performance_quarterly, hour: parseInt(e.target.value) || 9 } }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Minute</Label>
                    <Input type="number" min="0" max="59"
                           value={subscriptions.performance_quarterly?.minute ?? 0}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, performance_quarterly: { ...prev.performance_quarterly, minute: parseInt(e.target.value) || 0 } }))} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Recipients (comma-separated emails)</Label>
                  <Input value={recipientsInput.performance_quarterly}
                         onChange={(e) => setRecipientsInput(prev => ({ ...prev, performance_quarterly: e.target.value }))}
                         placeholder="people-ops@wisewit.ai" data-testid="recipients-performance" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => saveSubscription('performance_quarterly')} data-testid="save-performance-sub">Save</Button>
                  <Button size="sm" variant="outline" onClick={() => sendTestReport('performance_quarterly')} className="gap-1" data-testid="test-performance-sub">
                    <Send className="w-3.5 h-3.5" /> Send Test Now
                  </Button>
                </div>
              </div>

              {/* Attrition Monthly */}
              <div className="rounded-md border p-4 space-y-3" data-testid="sub-attrition">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Attrition Snapshot — Monthly</p>
                    <p className="text-xs text-muted-foreground">Last 30 days exits with reasons, tenure of leavers, and department hotspots</p>
                  </div>
                  <Switch
                    checked={!!subscriptions.attrition_monthly?.enabled}
                    onCheckedChange={(v) => toggleSubscription('attrition_monthly', v)}
                    data-testid="toggle-attrition-sub"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Day of Month</Label>
                    <Input type="number" min="1" max="28"
                           value={subscriptions.attrition_monthly?.day_of_month ?? 1}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, attrition_monthly: { ...prev.attrition_monthly, day_of_month: parseInt(e.target.value) || 1 } }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Hour (IST)</Label>
                    <Input type="number" min="0" max="23"
                           value={subscriptions.attrition_monthly?.hour ?? 9}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, attrition_monthly: { ...prev.attrition_monthly, hour: parseInt(e.target.value) || 9 } }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Minute</Label>
                    <Input type="number" min="0" max="59"
                           value={subscriptions.attrition_monthly?.minute ?? 0}
                           onChange={(e) => setSubscriptions(prev => ({ ...prev, attrition_monthly: { ...prev.attrition_monthly, minute: parseInt(e.target.value) || 0 } }))} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Recipients (comma-separated emails)</Label>
                  <Input value={recipientsInput.attrition_monthly}
                         onChange={(e) => setRecipientsInput(prev => ({ ...prev, attrition_monthly: e.target.value }))}
                         placeholder="people-ops@wisewit.ai, ceo@wisewit.ai" data-testid="recipients-attrition" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => saveSubscription('attrition_monthly')} data-testid="save-attrition-sub">Save</Button>
                  <Button size="sm" variant="outline" onClick={() => sendTestReport('attrition_monthly')} className="gap-1" data-testid="test-attrition-sub">
                    <Send className="w-3.5 h-3.5" /> Send Test Now
                  </Button>
                </div>
              </div>

            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setSubsOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ReportsPage;
